import {
    type FamilyMember,
    type MemberDataFirestoreRecord,
    Membership,
} from "@albertine/types";
import type {
    MemberDeviceLocation,
    Member as MemberDto,
    MemberFamilyMember,
    MemberFuturePlans,
} from "@albertine/types/openapi/lmt";
import { isoToFirebaseTimestamp } from "./timestamp.util";

export function mapApiMemberToMemberData({
    conversationId,
    assignedToId,
    archivedAt,
    archivedById,
    reactivatedById,
    ...dto
}: MemberDto): MemberDataFirestoreRecord {
    return {
        ...dto,
        conversation: conversationId,
        assignedTo: assignedToId,
        archivedDate: isoToFirebaseTimestamp(archivedAt),
        archivedBy: archivedById,
        reactivatedBy: reactivatedById,
        createdAt: isoToFirebaseTimestamp(dto.createdAt),
        lastSeenAt: isoToFirebaseTimestamp(dto.lastSeenAt),
        reactivatedAt: isoToFirebaseTimestamp(dto.reactivatedAt),
        onboardingCompletedAt: isoToFirebaseTimestamp(dto.onboardingCompletedAt),
        membership: toMembership(dto.membership),
        deviceLocation:
            dto.deviceLocation && toClientDeviceLocation(dto.deviceLocation),
        diningFuturePlans:
            dto.diningFuturePlans && toClientFuturePlans(dto.diningFuturePlans),
        travelFuturePlans:
            dto.travelFuturePlans && toClientFuturePlans(dto.travelFuturePlans),
        familyMembers: dto.familyMembers?.map(toClientFamilyMember),
    };
}

function toMembership(membership: MemberDto["membership"]): Membership | null {
    if (!membership) {
        return null;
    }

    const cancellation = membership.cancellation
        ? {
              ...membership.cancellation,
              cancelledAt: isoToFirebaseTimestamp(
                  membership.cancellation.cancelledAt ?? undefined,
              ),
          }
        : membership.cancellation;
    const parsed = Membership.safeParse({ ...membership, cancellation });
    return parsed.success ? parsed.data : null;
}

function toClientDeviceLocation(deviceLocation: MemberDeviceLocation) {
    return {
        ...deviceLocation,
        updatedAt: isoToFirebaseTimestamp(deviceLocation.updatedAt),
    };
}

function toClientFuturePlans({
    plans,
    updatedAt,
    updatedById,
    updatedName,
}: MemberFuturePlans) {
    return {
        plans,
        updatedAt: isoToFirebaseTimestamp(updatedAt),
        updatedBy: updatedById,
        updatedName,
    };
}

function toClientFamilyMember(familyMember: MemberFamilyMember): FamilyMember {
    switch (familyMember.type) {
        case "linked_profile": {
            const { memberId, ...linkedProfile } = familyMember;
            return { ...linkedProfile, member: memberId };
        }
        case "limited_profile":
            return familyMember;
        default: {
            const exhaustive: never = familyMember;
            return exhaustive;
        }
    }
}
